import React from "react";
import { Link } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaShieldAlt, 
  FaGavel, 
  FaUserCheck, 
  FaRocket,
  FaServer,
  FaEnvelope,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaUsers,
  FaLock,
  FaHandshake,
  FaCode,
  FaCreditCard,
  FaBalanceScale
} from "react-icons/fa";
import "../styles/TermsOfService.css";

function TermsOfService() {
  return (
    <div className="terms-container">
      <header className="terms-header">
        <div className="container">
          <Link to="/" className="back-link">
            <FaArrowLeft />
            <span>Back to Home</span>
          </Link>
          <div className="terms-hero">
            <h1>Terms of Service</h1>
            <p>
              Welcome to Triagz, the AI-powered endpoint security research platform. These Terms of Service govern your access to and use of our services, including our agent-based endpoint monitoring solutions.
            </p>
            <div className="last-updated">
              <FaCalendarAlt />
              <span>Last updated: January 15, 2025</span>
            </div>
          </div>
        </div>
      </header>

      <main className="terms-main">
        <div className="container">
          <div className="terms-content">
            {/* Agreement and Acceptance */}
            <section className="terms-section">
              <div className="section-header">
                <FaGavel className="section-icon" />
                <h2>1. Agreement to Terms</h2>
              </div>
              <div className="section-content">
                <p>
                  By accessing or using Triagz ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, then you may not access the Service.
                </p>
                <p>
                  Triagz is an AI-driven endpoint security research platform that enables security professionals to analyze and investigate endpoints through natural language prompts. Our platform connects to endpoint agents running on various operating systems (Windows, Linux, macOS) and provides AI-powered analysis through Third party LLM technologies.
                </p>
                <p>
                  If you are entering into these Terms on behalf of a company or organization, you represent that you have the legal authority to bind that entity to these terms.
                </p>
              </div>
            </section>

            {/* Access and Use */}
            <section className="terms-section">
              <div className="section-header">
                <FaUserCheck className="section-icon" />
                <h2>2. Access and Use</h2>
              </div>
              <div className="section-content">
                <h3>2.1 Service Description</h3>
                <p>
                  Triagz provides an AI-powered endpoint security research platform that allows users to:
                </p>
                <ul className="terms-list">
                  <li>Deploy and manage endpoint agents across multiple operating systems</li>
                  <li>Interact with endpoints through natural language queries</li>
                  <li>Perform security analysis and threat hunting activities</li>
                  <li>Generate automated reports and insights</li>
                </ul>
                
                <h3>2.2 Account Requirements</h3>
                <p>
                  To use Triagz, you must create an account and provide accurate, complete information. You are responsible for maintaining the security of your account credentials and for all activities that occur under your account.
                </p>
                
                <h3>2.3 Agent Installation and Management</h3>
                <p>
                  You may install Triagz agents on endpoints under your control or management. You represent and warrant that you have the necessary permissions to install and operate these agents on the target systems.
                </p>
              </div>
            </section>

            {/* User Responsibilities */}
            <section className="terms-section">
              <div className="section-header">
                <FaShieldAlt className="section-icon" />
                <h2>3. User Responsibilities</h2>
              </div>
              <div className="section-content">
                <h3>3.1 Acceptable Use</h3>
                <p>
                  You agree to use Triagz only for lawful security research, endpoint monitoring, and threat analysis purposes. You must not:
                </p>
                <ul className="terms-list">
                  <li>Use the Service to access systems without proper authorization</li>
                  <li>Violate any applicable laws, regulations, or third-party rights</li>
                  <li>Attempt to reverse engineer, modify, or tamper with the Service</li>
                  <li>Use the Service to distribute malware or engage in malicious activities</li>
                  <li>Share access credentials or agent keys with unauthorized parties</li>
                  <li>Use the Service in ways that could harm or compromise other users' systems</li>
                </ul>
                
                <h3>3.2 Data Security</h3>
                <p>
                  You are responsible for ensuring that your use of Triagz complies with your organization's data security policies and applicable privacy laws. You must obtain necessary permissions before analyzing systems containing sensitive or personal data.
                </p>
                
                <h3>3.3 System Integrity</h3>
                <p>
                  You agree to maintain the integrity of systems under your control and to promptly report any security incidents or vulnerabilities discovered through the use of Triagz.
                </p>
              </div>
            </section>

            {/* AI and Content */}
            <section className="terms-section">
              <div className="section-header">
                <FaCode className="section-icon" />
                <h2>4. AI Services and Content</h2>
              </div>
              <div className="section-content">
                <h3>4.1 AI-Generated Content</h3>
                <p>
                  Triagz uses Third party LLM technologies to analyze endpoint data and generate responses. AI-generated content may contain errors, inaccuracies, or be incomplete. You acknowledge that:
                </p>
                <ul className="terms-list">
                  <li>AI responses are generated automatically and may not always be accurate</li>
                  <li>You are responsible for verifying and validating all AI-generated insights</li>
                  <li>AI analysis should not be solely relied upon for critical security decisions</li>
                  <li>The AI may produce similar responses for different users</li>
                </ul>
                
                <h3>4.2 Your Content</h3>
                <p>
                  You retain ownership of the data you submit to Triagz. By using the Service, you grant us a limited license to process your data for the purpose of providing the Service. We will handle your data in accordance with our Privacy Policy.
                </p>
                
                <h3>4.3 Training and Improvement</h3>
                <p>
                  We may use aggregated, anonymized data from your usage to improve our AI models and services, except where you have explicitly opted out or where restricted by your account settings.
                </p>
              </div>
            </section>

            {/* Payment and Billing */}
            <section className="terms-section">
              <div className="section-header">
                <FaCreditCard className="section-icon" />
                <h2>5. Payment and Billing</h2>
              </div>
              <div className="section-content">
                <h3>5.1 Subscription Plans</h3>
                <p>
                  Triagz offers various subscription plans with different features and usage limits. All fees are non-refundable unless otherwise specified or required by law.
                </p>
                
                <h3>5.2 Billing</h3>
                <p>
                  Subscription fees are billed in advance on a recurring basis. You authorize us to charge your designated payment method for all fees incurred under your account.
                </p>
                
                <h3>5.3 Changes to Pricing</h3>
                <p>
                  We reserve the right to modify our pricing with reasonable advance notice. Price changes will not affect your current billing cycle but will apply to subsequent renewals.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="terms-section">
              <div className="section-header">
                <FaBalanceScale className="section-icon" />
                <h2>6. Intellectual Property</h2>
              </div>
              <div className="section-content">
                <h3>6.1 Triagz Property</h3>
                <p>
                  The Service, including all software, algorithms, and documentation, is owned by Triagz and protected by intellectual property laws. We grant you a limited, non-exclusive license to use the Service according to these Terms.
                </p>
                
                <h3>6.2 Your Data</h3>
                <p>
                  You retain all rights to your data and endpoint information. We do not claim ownership of your data, but you grant us necessary rights to provide the Service.
                </p>
                
                <h3>6.3 Feedback</h3>
                <p>
                  Any feedback, suggestions, or ideas you provide about the Service become the property of Triagz and may be used to improve our offerings.
                </p>
              </div>
            </section>

            {/* Privacy and Data Protection */}
            <section className="terms-section">
              <div className="section-header">
                <FaLock className="section-icon" />
                <h2>7. Privacy and Data Protection</h2>
              </div>
              <div className="section-content">
                <p>
                  Your privacy is important to us. Our collection, use, and protection of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.
                </p>
                <p>
                  By using Triagz, you consent to the collection and processing of your data as described in our Privacy Policy, including the transmission of endpoint data to our servers for AI analysis.
                </p>
              </div>
            </section>

            {/* Disclaimers */}
            <section className="terms-section">
              <div className="section-header">
                <FaExclamationTriangle className="section-icon" />
                <h2>8. Disclaimers</h2>
              </div>
              <div className="section-content">
                <p>
                  <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</strong> We disclaim all warranties, express or implied, including but not limited to:
                </p>
                <ul className="terms-list">
                  <li>Warranties of merchantability and fitness for a particular purpose</li>
                  <li>Accuracy, reliability, or completeness of AI-generated content</li>
                  <li>Uninterrupted or error-free operation of the Service</li>
                  <li>Security of data transmission or storage</li>
                </ul>
                <p>
                  You acknowledge that endpoint security involves inherent risks and that no security tool can guarantee complete protection against all threats.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="terms-section">
              <div className="section-header">
                <FaHandshake className="section-icon" />
                <h2>9. Limitation of Liability</h2>
              </div>
              <div className="section-content">
                <p>
                  To the maximum extent permitted by law, Triagz shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities.
                </p>
                <p>
                  Our total liability to you for any claim arising out of or relating to these Terms or the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
                </p>
                <p>
                  Some jurisdictions do not allow the exclusion or limitation of certain damages, so these limitations may not apply to you.
                </p>
              </div>
            </section>

            {/* Termination */}
            <section className="terms-section">
              <div className="section-header">
                <FaUsers className="section-icon" />
                <h2>10. Termination</h2>
              </div>
              <div className="section-content">
                <p>
                  You may terminate your account at any time through your account settings. We may terminate or suspend your account immediately if you violate these Terms.
                </p>
                <p>
                  Upon termination, your right to use the Service ceases immediately. We may delete your account data after a reasonable period, subject to our data retention policies and legal obligations.
                </p>
                <p>
                  Sections relating to liability, intellectual property, and dispute resolution will survive termination.
                </p>
              </div>
            </section>

            {/* Dispute Resolution */}
            <section className="terms-section">
              <div className="section-header">
                <FaGavel className="section-icon" />
                <h2>11. Dispute Resolution</h2>
              </div>
              <div className="section-content">
                <p>
                  Any disputes arising from these Terms will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. The arbitration will be conducted in English and located in a mutually agreed jurisdiction.
                </p>
                <p>
                  You waive the right to participate in class action lawsuits or class-wide arbitrations against Triagz.
                </p>
              </div>
            </section>

            {/* General Provisions */}
            <section className="terms-section">
              <div className="section-header">
                <FaServer className="section-icon" />
                <h2>12. General Provisions</h2>
              </div>
              <div className="section-content">
                <h3>12.1 Governing Law</h3>
                <p>
                  These Terms are governed by the laws of the jurisdiction where Triagz is headquartered, without regard to conflict of law principles.
                </p>
                
                <h3>12.2 Changes to Terms</h3>
                <p>
                  We may modify these Terms at any time. Material changes will be communicated to you via email or through the Service. Your continued use of the Service after changes constitute acceptance of the modified Terms.
                </p>
                
                <h3>12.3 Severability</h3>
                <p>
                  If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
                </p>
                
                <h3>12.4 Entire Agreement</h3>
                <p>
                  These Terms, together with our Privacy Policy, constitute the entire agreement between you and Triagz regarding the Service.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="terms-section">
              <div className="section-header">
                <FaEnvelope className="section-icon" />
                <h2>13. Contact Information</h2>
              </div>
              <div className="section-content">
                <p>
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="contact-info">
                  <div className="contact-item">
                    <strong>Email:</strong> admin@triagz.com
                  </div>
                  <div className="contact-item">
                    <strong>Support:</strong> support@triagz.com
                  </div>
                  <div className="contact-item">
                    <strong>Response Time:</strong> We aim to respond to all inquiries within 48 hours
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>
    </div>
  );
}

export default TermsOfService; 